import { html, TemplateResult, LitElement, nothing } from 'lit';
import { property, state } from 'lit/decorators.js';
import { Swiper } from 'swiper';
import { AnalyticsService } from '@dertour/common-lib';
import { renderSliderControls } from '../templates/slider-controls.js';
import { ImpressionsList } from '../types/types.js';
import { SwiperService } from '../services/swiper.service.js';
import { SliderUtils } from '../utils/slider.utils.js';
import { dispatchWarning } from '@dertour/common-lib';
import { localized } from '@lit/localize';

/**
 * Slider wrapper component for DERTOUR Impressions module
 */
@localized()
export class SliderWrapper extends LitElement {
    protected override createRenderRoot(): HTMLElement | DocumentFragment {
        return this; // Light DOM – render inside parent's Shadow DOM
    }

    @property({ type: Object })
    impressionsList: ImpressionsList | undefined;

    @property({ type: String, attribute: 'text-position' })
    textPosition: 'left' | 'right' = 'left';

    @property({ attribute: 'ab-variant' })
    abVariant: 'a' | 'b' = 'a';

    @state()
    nextSlidePreviewUrl = '';

    @state()
    prevSlidePreviewUrl = '';

    @state()
    currentSlideIndex = 0;

    private imageSwiper: Swiper | null = null;
    private textSwiper: Swiper | null = null;
    private readonly _onResize = () => this._updateTextSwiperHeight();
    /**
     * Set preview images for next and previous slides
     * @private
     */
    private _setPreviewImages(): void {
        if (!this.imageSwiper || !this.impressionsList) {
            this.nextSlidePreviewUrl = '';
            this.prevSlidePreviewUrl = '';
            return;
        }

        const currentIndex = this.imageSwiper.realIndex;
        const previewImages = SliderUtils.calculatePreviewImages(currentIndex, this.impressionsList);

        this.currentSlideIndex = previewImages.currentIndex;
        this.nextSlidePreviewUrl = previewImages.nextUrl;
        this.prevSlidePreviewUrl = previewImages.prevUrl;
    }

    /**
     * Compute the tallest text slide and set the text-swiper height accordingly
     */
    private _updateTextSwiperHeight(): void {
        const container = this.renderRoot.querySelector('.swiper.swiper-text') as HTMLElement | null;
        if (!container) return;

        const slides = Array.from(container.querySelectorAll('.swiper-slide')) as HTMLElement[];
        if (slides.length === 0) return;

        // Reset to auto before measuring to get natural heights
        container.style.height = 'auto';
        let max = 0;
        slides.forEach((slide) => {
            // Ensure slide uses natural height for measurement
            const prev = slide.style.height;
            slide.style.height = 'auto';
            const h = Math.ceil(slide.getBoundingClientRect().height);
            if (h > max) max = h;
            slide.style.height = prev;
        });

        if (max > 0) {
            container.style.height = `${max}px`;
        }
    }

    /**
     * Attach global listeners
     */
    connectedCallback(): void {
        super.connectedCallback();
        window.addEventListener('resize', this._onResize);
    }

    /**
     * Cleanup global listeners
     */
    disconnectedCallback(): void {
        window.removeEventListener('resize', this._onResize);
        super.disconnectedCallback();
    }

    /**
     * Lifecycle method called after component updates
     * Initializes Swiper when impressionsList is loaded
     * @param changed Changed properties map
     */
    protected updated(changed: Map<string, unknown>): void {
        // Initialize Swiper once impressionsList is loaded and swiper not yet initialized
        if (changed.has('impressionsList') && this.impressionsList && !this.imageSwiper) {
            // Use setTimeout to ensure DOM is fully updated
            setTimeout(() => {
                const container = this.renderRoot.querySelector('.swiper-image');
                const textContainer = this.renderRoot.querySelector('.swiper-text');

                if (container instanceof HTMLElement) {
                    this.imageSwiper = SwiperService.initialize(container, {
                        onRealIndexChange: () => {
                            this._setPreviewImages();
                            if (this.textSwiper && this.imageSwiper) {
                                this.textSwiper.slideToLoop(this.imageSwiper.realIndex, 0);
                            }
                        },
                    });
                    // Set initial preview images
                    this._setPreviewImages();
                } else {
                    dispatchWarning(this, 'SwiperInitialization', {
                        message: 'Swiper container not found in renderRoot.',
                    });
                }

                if (textContainer instanceof HTMLElement) {
                    this.textSwiper = SwiperService.initialize(textContainer, {
                        allowTouchMove: false,
                        autoHeight: false,
                        speed: 0,
                    });
                }

                // Link controllers when both swipers exist and align initial index
                if (this.imageSwiper && this.textSwiper) {
                    this.textSwiper.slideToLoop(this.imageSwiper.realIndex, 0);
                    // Defer height calc to next frame for correct layout metrics
                    requestAnimationFrame(() => this._updateTextSwiperHeight());
                    // Recalculate after fonts load (if supported), as typography can change heights
                    const docWithFonts = document as unknown as { fonts?: FontFaceSet };
                    if (docWithFonts.fonts?.ready) {
                        docWithFonts.fonts.ready.then(() => this._updateTextSwiperHeight());
                    }
                }
            }, 0);
        }
    }

    /**
     * Handle previous slide button click
     * @private
     */
    private _handlePrevClick(): void {
        if (this.imageSwiper) {
            SwiperService.slidePrev(this.imageSwiper);
            this._triggerAnalyticsEvent('prev');
        }
    }

    /**
     * Handle next slide button click
     * @private
     */
    private _handleNextClick(): void {
        if (this.imageSwiper) {
            SwiperService.slideNext(this.imageSwiper);
            this._triggerAnalyticsEvent('next');
        }
    }

    /**
     * Handle keyboard events for accessibility
     * @param event Event object
     * @param handler Event handler function
     * @private
     */
    private _handleKeyDown(event: KeyboardEvent, handler: () => void): void {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handler();
        }
    }

    /**
     * Trigger analytics event
     * @param direction Direction of the event ('prev' or 'next')
     * @private
     */
    private _triggerAnalyticsEvent(direction: 'prev' | 'next'): void {
        const currentIndex = this.imageSwiper?.realIndex ?? 0;
        const totalSlides = this.impressionsList?.items.length ?? 0;

        const eventData = AnalyticsService.buildSliderEvent(
            direction,
            direction === 'prev' ? currentIndex - 1 : currentIndex + 1,
            totalSlides,
            this.tagName.toLowerCase()
        );

        AnalyticsService.dispatch(this, eventData);
    }

    /**
     * Render the slider component
     * @returns Template result
     */
    protected render(): TemplateResult {
        if (!this.impressionsList || !this.impressionsList.items || this.impressionsList.items.length === 0) {
            return html``;
        }

        return html`
            <div class="impressions-wrapper" data-text-position=${this.textPosition} data-ab-variant=${this.abVariant}>
                <div class="impressions-container">
                    <!-- Left Side: Text and Controls -->
                    <div class="left-side">
                        <div class="text-content">
                            <p
                                class="custom-pagination swiper-pagination-fraction swiper-pagination-horizontal der-tm-tt-index"
                            >
                                <span class="swiper-pagination-current"
                                    >${String(this.currentSlideIndex + 1).padStart(2, '0')}</span
                                >
                                <span class="fraction-sign">/</span>
                                <span class="swiper-pagination-total"
                                    >${String(this.impressionsList.items.length).padStart(2, '0')}</span
                                >
                            </p>
                            <div class="swiper swiper-text">
                                <div class="swiper-wrapper">
                                    ${this.impressionsList.items.map(
                                        (slide) => html`
                                            <div class="swiper-slide">
                                                <h3 class="title">${slide.title}</h3>
                                                <p class="description">${slide.text}</p>
                                            </div>
                                        `
                                    )}
                                </div>
                            </div>
                            ${renderSliderControls(
                                this.prevSlidePreviewUrl,
                                this.nextSlidePreviewUrl,
                                this._handlePrevClick.bind(this),
                                this._handleNextClick.bind(this),
                                this._handleKeyDown.bind(this)
                            )}
                        </div>
                    </div>

                    <!-- Right Side: Swiper -->
                    <div class="right-side">
                        <div class="swiper swiper-image">
                            <div class="swiper-wrapper">
                                ${this.impressionsList.items.map(
                                    (slide, index) => html`
                                        <div class="swiper-slide">
                                            <img
                                                src="${slide.img}"
                                                alt="${slide.title}"
                                                fetchpriority=${index === 0 ? 'high' : nothing}
                                            />
                                        </div>
                                    `
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        `;
    }
}

if (!customElements.get('der-tm-impressions-slider-wrapper')) {
    customElements.define('der-tm-impressions-slider-wrapper', SliderWrapper);
}
